import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import { useRoute } from '@vmos2/utils';
import { useDeviceSize, useConfig, useSettings, useUser } from '@vmos2/providers';
import { Button, Icon, Link } from '@vmos2/design-system';

import { selectors } from '../../../selectors';
import { Rewards } from './rewards';
import { LocaleSwitcher } from './localeSwitcher';

export const UserButton = () => {
  const { t } = useTranslation('header');
  const { route } = useRoute();
  const { bootstrap } = useConfig();
  const { isGuest, user: { userData }, isLoggedIn } = useUser();
  const { localeSwitcher: isLocaleSwitcherEnabled } = useSettings();
  const { isMobile } = useDeviceSize();

  const authAction = selectors.authAction(bootstrap.settings);

  const userName = useMemo(() => get(userData, 'profile.firstName', t('noName')), [userData]);

  const routeWithoutLayout = useMemo(() => {
    return route.name.substring(route.name.indexOf('.') + 1);
  }, [route.name]);

  const routeParams = useMemo(() => {
    return { fromRoute: routeWithoutLayout, fromParams: JSON.stringify(route.params) };
  }, [routeWithoutLayout, route]);

  const renderUserName = isLocaleSwitcherEnabled && isMobile ? null : <Name>{userName}</Name>;

  return (
    <Container>
      {!isGuest ? (
        <>
          <LocaleSwitcher />
          {isLoggedIn && <Rewards routeParams={routeParams} />}

          <Button
            iconLeft={<Icon name="User" />}
            size="small"
            as={Link}
            mode="ghost"
            color="dark"
            to={isLoggedIn ? 'profile.orders-history' : `account.${authAction || 'auth.login'}`}
            params={routeParams}
            dataTest={userName ? 'header-profile-link' : 'header-auth-link'}
            removeMargin={isLocaleSwitcherEnabled}
          >
            {isLoggedIn ? renderUserName : t('login')}
          </Button>
        </>
      ) : null}
    </Container>
  );
};

const Container = styled('div')`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const Name = styled('span')`
  white-space: nowrap;
  max-width: 86px;
  min-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    width: auto;
  }
`;

UserButton.defaultProps = {
  count: 0,
};
